import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Download, Lock, Wallet } from "lucide-react";

import { api, downloadProtected } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { CommissionItem } from "@/components/commission-item";
import { TopBar } from "@/components/header";
import { Button, ChipRow, Empty, ErrorView, Field, Loading } from "@/components/kit";
import { Sheet } from "@/components/sheet";
import { useToast } from "@/components/toast";
import { C, eur } from "@/lib/format";

const FILTERS = [
  { key: "pending", label: "À valider" },
  { key: "validated", label: "À payer" },
  { key: "paid", label: "Payées" },
  { key: "all", label: "Toutes" },
  { key: "referral_bonus", label: "Parrainage" },
];

export default function AdminFinance() {
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();
  const { user } = useAuth();
  const isSuper = !!user && user.permission === "super";
  const [f, setF] = useState("pending");
  const [payFor, setPayFor] = useState(null);
  const [ref, setRef] = useState("");
  const [busy, setBusy] = useState(null);
  const q = useQuery({ queryKey: ["admin-commissions"], queryFn: () => api("/admin/commissions"), enabled: isSuper });

  const items = (q.data?.items ?? []).filter((c) => (f === "all" ? true : f === "referral_bonus" ? c.type === "referral_bonus" : c.status === f));

  const act = async (id, path, body, msg = "") => {
    setBusy(id);
    try {
      await api(path, { method: "POST", body });
      await q.refetch();
      qc.invalidateQueries({ queryKey: ["admin-dashboard"] });
      toast(msg);
      return true;
    } catch (e) {
      toast(e.message, "error");
      return false;
    } finally {
      setBusy(null);
    }
  };

  if (!isSuper) {
    return (
      <div data-testid="admin-finance-screen" className="p-6">
        <Empty icon={Lock} title="Accès réservé" text="La gestion financière est réservée à l'administrateur principal." />
      </div>
    );
  }

  return (
    <div data-testid="admin-finance-screen" className="flex flex-col">
      <TopBar
        title="Finances"
        right={<Button testID="finance-export-csv-button" title="Export CSV" icon={Download} variant="secondary" onClick={() => downloadProtected("/admin/commissions/export.csv", "commissions-ma-maison-suivie.csv").then(() => toast("Export CSV téléchargé.")).catch((e) => toast(e.message, "error"))} />}
      >
        <ChipRow options={FILTERS} value={f} onChange={setF} testIDPrefix="finance-filter" />
      </TopBar>
      {q.isLoading ? <Loading /> : q.isError || !q.data ? <ErrorView message="Erreur de chargement." onRetry={() => q.refetch()} /> : (
        <div className="mx-auto flex w-full max-w-6xl flex-col gap-4 p-4 pb-8 md:p-8">
          <div className="grid grid-cols-3 gap-3">
            {[
              { id: "pending", l: "À valider", v: q.data.totals.pending, c: C.warning },
              { id: "validated", l: "À payer", v: q.data.totals.validated, c: C.brand },
              { id: "paid", l: "Payé", v: q.data.totals.paid, c: C.success },
            ].map((x) => (
              <div key={x.id} className="rounded-xl border border-[#333333] bg-[#1E1E1E] p-3 md:p-4">
                <p className="t-caption">{x.l}</p>
                <p data-testid={`finance-total-${x.id}`} className="t-h3 md:!text-xl" style={{ color: x.c }}>{eur(x.v)}</p>
              </div>
            ))}
          </div>
          {items.length === 0 ? <Empty icon={Wallet} title="Rien à afficher" text="Aucune commission dans cette catégorie." /> : (
            <div className="grid gap-3 lg:grid-cols-2">
              {items.map((item) => (
                <CommissionItem key={item.id} c={item} showAgent>
                  <div className="mt-2 flex gap-2">
                    <Button testID={`finance-open-agent-${item.id}`} title="Fiche agent" variant="secondary" className="flex-1" onClick={() => navigate(`/admin/agent/${item.agent_id}`)} />
                    {item.status === "pending" && (
                      <Button testID={`finance-validate-${item.id}`} title="Valider" variant="success" className="flex-1" loading={busy === item.id}
                        onClick={() => act(item.id, `/admin/commissions/${item.id}/validate`, undefined, "Commission validée.")} />
                    )}
                    {item.status === "validated" && (
                      <Button testID={`finance-pay-${item.id}`} title="Marquer payée" className="flex-1" onClick={() => { setRef(""); setPayFor(item); }} />
                    )}
                  </div>
                </CommissionItem>
              ))}
            </div>
          )}
        </div>
      )}
      <Sheet visible={!!payFor} onClose={() => setPayFor(null)} title="Enregistrer le paiement" testID="pay-sheet">
        {payFor && (
          <div className="flex flex-col gap-4">
            <p className="t-body">{payFor.agent_name} · {eur(payFor.amount)} · {payFor.recommendation_number}</p>
            {payFor.agent_has_rib === false && <p className="text-sm text-[#F2994A]">Attention : cet agent n'a pas encore renseigné de RIB.</p>}
            <Field testID="pay-reference-input" label="Référence du virement (facultatif)" value={ref} onChangeText={setRef} />
            <Button testID="pay-confirm-button" title="Confirmer le paiement" loading={busy === payFor.id}
              onClick={async () => { if (await act(payFor.id, `/admin/commissions/${payFor.id}/pay`, { reference: ref || null }, "Paiement enregistré.")) setPayFor(null); }} />
          </div>
        )}
      </Sheet>
    </div>
  );
}
